import * as moment from 'moment';
import { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Dispatch, connect } from 'react-redux';
import { IWatrReimbursement, IWatrRequest, IWatrRequestTravels } from '@nkr/models/index';
import { Button, Col, Row, Label, OverlayTrigger, Tooltip, Glyphicon, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FileDocumentType } from '@nkr/enums/document-type';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { VerifyWrapper } from '../../styles';
import { DonorShieldContext } from '../context';
import { IApplicationState, WatrActionCreators } from '@nkr/store/index';
import Header from '../header';
import VerificationStepTracker from '../verification-step-tracker';
import DonorExpenseRequestSummary from './donor-expense-request-summary';
import { IPriorTotals } from '../prior-totals';
import { Override } from './override';
import { IWatrTrip } from '@nkr/models/watr-trip';
import { IProps, IDispatchProps, TProps } from './types/verify-travel.types';
import { ILineItem } from './types/line-item.types';
import { ITripGroup } from './types/trip.types';
import { useTripsData } from './hooks/useTripsData';
import { useReceiptFiles } from './hooks/useReceiptFiles';
import { useTripNavigation } from './hooks/useTripNavigation';
import { useReviewState } from './hooks/useReviewState';
import { useOverrideState } from './hooks/useOverrideState';
import { useSidebarState } from './hooks/useSidebarState';
import { useSidebarController } from './hooks/useSidebarController';
import { useTripApproval } from './hooks/useTripApproval';
import { useClaimVerification } from './hooks/useClaimVerification';
import { groupTripsByPhase, findFirstPendingTripIndex, findNextPendingTrip } from './services/trip.service';
import { buildLineItemUpdates } from './services/lineItem.service';
import { recalculateGrandTotal, getPriorTotals, calculateTripStats, IGrandTotal } from './utils/calculation.utils';
import { calculateExpenseTotals, sortLineItemsBySummaryOrder, sumMileageLineItemAmount, sumMileageRoundTripMiles, deriveMileageRateFromLineItems, tripHasReviewableContent } from './utils/expense.utils';
import {
    calculateTotalMilesReimbursementAmountWithOverride,
    calculateTotalCarServiceExpenseReimbursementAmountWithOverride,
    calculateTotalMealsReimbursementAmountWithOverride,
    calculateTotalDependentCarExpenseReimbursementAmountWithOverride,
    calculateRawRequestedAmounts
} from './utils/approved-amounts.utils';
import { filterAddress, findPendingTravelRequest, getPriorClosedTravelClaims, hasPriorClosedTravelClaims } from './utils/trip.utils';
import { getStatusLabel } from './constants/trip-status';
import { resolveMileageRate } from '../year-rates';
import { areAllTripsResolved, shouldTransitionToOverall, getInitialNavigationState } from './utils/trip-navigation.utils';
import { resetSidebarState } from './utils/sidebar-reset.utils';
import { useAutoNavigation } from './hooks/useAutoNavigation';
import { ExpenseType } from './constants/expense-types';
import ConnectedDevMockDocsOffer from '../dev-mock-docs/ConnectedDevMockDocsOffer';
import { checkTripApprovalDisabled, checkClaimApprovalDisabled, isApproveDisabled, isDeclineDisabled } from './utils/approval.utils';
import { TripPhaseTabs } from './components/TripPhaseTabs';
import { TripStatsCards } from './components/TripStatsCards';
import { CaregiverInfoCard } from './components/CaregiverInfoCard';
import { DependentCareInfoCard } from './components/DependentCareInfoCard';
import { WageAndTravelStatus, ReimbursementType } from '@nkr/enums/index';
import { readAdvancedProgressForBuckets, resolveDerProgramCap } from '../advanced-reimbursement-status-buckets';
import ConfirmUnpayableDecline from '../modals/confirm-unpayable-decline';
import ClaimHistoryModal from '../modals/claim-history-modal';
const hasDonorAdvancedReimbursement = (request: IWatrRequest | null | undefined): boolean => {
    if (!request) {
        return false;
    }
    const progress = readAdvancedProgressForBuckets(request);
    if (progress?.onTrack === true || progress?.invitePending === true || progress?.invited === true) {
        return true;
    }
    if (request.status === WageAndTravelStatus.PendingAdvancedReimbursement) {
        return true;
    }
    const reimbursements = request.reimbursements || [];
    return reimbursements.some(r =>
        r != null
        && (r.reimbursementType === ReimbursementType.AdvancedReimbursement
            || r.reimbursementType === ReimbursementType.AdvancedReimbursementPreOp
            || r.reimbursementType === ReimbursementType.AdvancedReimbursementDonation
            || r.reimbursementType === ReimbursementType.InternationalWorkup));
};
const VerifyTravel = (props: TProps) => {
    const context = useContext(DonorShieldContext);
    const [grandTotal, setGrandTotal] = useState<IGrandTotal>({ 
        pendingApprovedTotal: 0, 
        currentlyApprovedTotal: 0,
        caregiverPendingApprovedTotal: 0,
        caregiverCurrentlyApprovedTotal: 0
    });
    const [previousTripGroups, setPreviousTripGroups] = useState<ITripGroup[]>([]);
    const [lastOpenedTripId, setLastOpenedTripId] = useState<number | null>(null);
    const [isManualSelection, setIsManualSelection] = useState(false);
    const [unpayableClaimConfirm, setUnpayableClaimConfirm] = useState<{ show: boolean; requestId: number; travelId: number }>({
        show: false,
        requestId: 0,
        travelId: 0
    });
    const [unpayableTripConfirm, setUnpayableTripConfirm] = useState<{
        show: boolean;
        tripId: number;
        requestId: number;
        tripIndex: number;
    }>({
        show: false,
        tripId: 0,
        requestId: 0,
        tripIndex: 0
    });
    const [showClaimHistoryModal, setShowClaimHistoryModal] = useState(false);

    const reviewState = useReviewState();
    const overrideStateHook = useOverrideState();
    
    const receiptFiles = useReceiptFiles({
        fetchFileUrl: context.fetchFileUrl
    });

    const tripsData = useTripsData({
        getTripsByTravelRequestId: props.getTripsByTravelRequestId,
        getLineItemsByTripId: props.getLineItemsByTripId,
        onProcessLineItems: reviewState.processItems,
        onRecalculateGrandTotal: setGrandTotal,
        onTripsLoaded: async () => {}
    });

    const navigation = useTripNavigation(tripsData.tripGroups);

    const pendingTravelRequest = useMemo(() => {
        return findPendingTravelRequest(props.data?.requestTravels || []);
    }, [props.data?.requestTravels]);

    const receiptInputsForMocks = useMemo(() => {
        const expenseTypeNames: { [key: number]: string } = {
            1: 'Lodging',
            2: 'Flight / Train',
            3: 'Transportation',
            4: 'Dependent Care',
            5: 'Meals',
            6: 'Car Service',
            7: 'Mileage'
        };
        const inputs: Array<{
            fileId: number;
            expenseType?: number;
            expenseTypeName?: string;
            amount?: number;
            description?: string;
            tripName?: string;
            expenseDate?: string;
            receiptFileName?: string;
        }> = [];
        const seen: { [k: number]: boolean } = {};
        (tripsData.tripGroups || []).forEach((group: any) => {
            (group.lineItems || []).forEach((li: any) => {
                const fileId = li.receiptFileId;
                if (!fileId || seen[fileId]) {
                    return;
                }
                seen[fileId] = true;
                const typeId = Number(li.expenseType);
                inputs.push({
                    fileId,
                    expenseType: typeId,
                    expenseTypeName: expenseTypeNames[typeId] || 'Expense',
                    amount: li.amount,
                    description: li.description,
                    tripName: group.name || group.type,
                    expenseDate: li.expenseDate,
                    receiptFileName: li.receiptFileName
                });
            });
        });
        return inputs;
    }, [tripsData.tripGroups]);

    const receiptFileIdsForMocks = useMemo(
        () => receiptInputsForMocks.map(r => r.fileId),
        [receiptInputsForMocks]
    );

    const refreshLineItems = useCallback(async () => {
        if (pendingTravelRequest?.id) {
            await tripsData.loadTrips(pendingTravelRequest.id, false);
        }
    }, [pendingTravelRequest, tripsData]);

    const sidebarState = useSidebarState({
        receiptFileUrls: receiptFiles.receiptFileUrls,
        fetchFileUrl: receiptFiles.fetchFileUrl,
        aggregateReceiptFiles: receiptFiles.aggregateReceiptFiles,
        onShowDataSideBar: context.onShowDataSideBar,
        onSetReviewRequiredBulk: context.onSetReviewRequiredBulk,
        onReviewRequiredStateChange: reviewState.updateReviewRequired,
        refreshLineItems: refreshLineItems
    });

    const sidebarController = useSidebarController({
        onOpenSidebar: sidebarState.openSidebar,
        onCloseSidebar: sidebarState.closeSidebar,
        onNavigateToTrip: (index: number, viewType: 'trip' | 'overall') => {
            navigation.setSelectedTripIndex(index);
            navigation.setViewType(viewType);
        },
        onSetLastOpenedTripId: setLastOpenedTripId
    });

    const autoNavigation = useAutoNavigation({
        tripGroups: tripsData.tripGroups,
        isLoading: tripsData.isLoading,
        navigation: navigation,
        sidebarState: sidebarState,
        sidebarController: sidebarController,
        setLastOpenedTripId: setLastOpenedTripId
    });

    const tripApproval = useTripApproval({
        approveTrip: props.approveTrip,
        declineTrip: props.declineTrip,
        onTripsReload: async () => {
            if (pendingTravelRequest?.id) {
                return await tripsData.loadTripsAndReturnGroups(pendingTravelRequest.id);
            }
            return undefined;
        },
        lineItemState: reviewState.lineItemState,
        reviewRequiredState: reviewState.reviewRequiredState,
        onCloseSidebar: sidebarState.closeSidebar,
        onAfterApprovalOrDecline: autoNavigation.navigateAfterApprovalOrDecline
    });

    const claimVerification = useClaimVerification({
        verifyTravel: props.verifyTravel
    });

    const isSwitchingTrips = autoNavigation.isNavigating || tripApproval.isApprovingTrip || tripApproval.isDecliningTrip;

    useEffect(() => {
        const loadData = async () => {
            if (!props.data) {
                tripsData.setIsLoading(false);
                return;
            }

            sidebarController.resetForNewRequest(props.data.id);

            sidebarState.setApprovedExpenseTypes(new Set());
            sidebarState.setApprovedLineItemIds(new Set());

            if (!pendingTravelRequest) {
                tripsData.setIsLoading(false);
                return;
            }

            const travelRequestId = pendingTravelRequest.id;
            
            if (pendingTravelRequest.trips && Array.isArray(pendingTravelRequest.trips) && pendingTravelRequest.trips.length > 0) {
                const tripsArray = pendingTravelRequest.trips.filter(trip => trip.travelRequestId === pendingTravelRequest.id);
                    
                    const lineItemsMap = new Map<number, ILineItem[]>();
                    for (const trip of tripsArray) {
                        try {
                            const lineItemsResponse = await props.getLineItemsByTripId(trip.id);
                            const items = (lineItemsResponse.data?.Results || lineItemsResponse.data?.results) as ILineItem[] || [];
                            lineItemsMap.set(trip.id, items);
                            reviewState.processItems(items);
                        } catch (err) {
                            lineItemsMap.set(trip.id, []);
                        }
                    }
                    const groups = groupTripsByPhase(tripsArray, lineItemsMap);
                    tripsData.setTripGroups(groups);
                    tripsData.setIsLoading(false);
            } else if (travelRequestId) {
                await tripsData.loadTrips(travelRequestId);
            } else {
                tripsData.setIsLoading(false);
            }
        };

        loadData();
    }, [props.data, pendingTravelRequest]);

    useEffect(() => {
        if (tripsData.tripGroups.length > 0) {
            const groupsChanged = tripsData.tripGroups.length !== previousTripGroups.length || 
                tripsData.tripGroups.some((g, idx) => {
                    const prev = previousTripGroups[idx];
                    return !prev || prev.id !== g.id || prev.status !== g.status;
                });
            
            if (groupsChanged) {
                const isInitialLoad = previousTripGroups.length === 0;
                setPreviousTripGroups(tripsData.tripGroups);
                
                const grandTotalValue = recalculateGrandTotal(tripsData.tripGroups);
                setGrandTotal(grandTotalValue);
                
                const shouldTransition = shouldTransitionToOverall(tripsData.tripGroups);
                
                if (shouldTransition) {
                    navigation.setViewType('overall');
                    if (navigation.selectedTripIndex >= tripsData.tripGroups.length) {
                        navigation.setSelectedTripIndex(0);
                    }
                } else if (isInitialLoad || navigation.selectedTripIndex >= tripsData.tripGroups.length) {
                    const navState = getInitialNavigationState(tripsData.tripGroups);
                    navigation.setSelectedTripIndex(navState.selectedIndex);
                    navigation.setViewType(navState.viewType);
                }
            }
        } else if (tripsData.tripGroups.length === 0 && !tripsData.isLoading) {
            if (previousTripGroups.length > 0) {
                setPreviousTripGroups([]);
            }
        }
    }, [tripsData.tripGroups, previousTripGroups, tripsData.isLoading, navigation]);

    useEffect(() => {
        if (tripsData.isLoading || tripsData.tripGroups.length === 0) {
            return;
        }

        if (isManualSelection) {
            return;
        }

        if (sidebarController.isManualOpenInProgress()) {
            return;
        }

        if (navigation.viewType === 'overall' && areAllTripsResolved(tripsData.tripGroups)) {
            return;
        }

        const selectedTrip = tripsData.tripGroups[navigation.selectedTripIndex];
        if (selectedTrip && sidebarController.isSidebarOpen && lastOpenedTripId === selectedTrip.id) {
            return;
        }

        const context = {
            tripGroups: tripsData.tripGroups,
            selectedTripIndex: navigation.selectedTripIndex,
            viewType: navigation.viewType,
            isLoading: tripsData.isLoading,
            previousTripGroups: previousTripGroups,
            lastOpenedTripId: lastOpenedTripId,
            requestId: props.data?.id || 0
        };

        const decision = sidebarController.evaluateSidebarState(context);
        sidebarController.executeDecision(decision, props.data?.id || 0, lastOpenedTripId);
    }, [
        tripsData.tripGroups,
        tripsData.isLoading,
        navigation.selectedTripIndex,
        navigation.viewType,
        previousTripGroups,
        lastOpenedTripId,
        props.data?.id,
        isManualSelection,
        sidebarController.isSidebarOpen,
        sidebarController.isManualOpenInProgress,
        sidebarController
    ]);

    useEffect(() => {
        if (navigation.viewType !== 'overall') {
            return;
        }

        sidebarState.setActiveExpenseType(null);
        sidebarController.handleManualClose();

        if (context?.onHideDataSideBar) {
            context.onHideDataSideBar();
        }
    }, [navigation.viewType]);

    useEffect(() => {
        if (navigation.viewType === 'trip') {
            return;
        }

        if (tripsData.tripGroups.length === 0 || navigation.selectedTripIndex < 0 || navigation.selectedTripIndex >= tripsData.tripGroups.length) {
            overrideStateHook.setOverrideState({
                isMilesOverWritten: false,
                overrideMiles: 0,
                isMealsOverWritten: false,
                overrideMeals: 0,
                isCarServiceExpenseOverridden: false,
                overrideCarServiceExpense: 0,
                isDependentCareExpenseOverridden: false,
                overrideDependentCareExpense: 0,
                isHotelLodgingExpenseOverridden: false,
                overrideHotelLodgingExpense: 0
            });
            return;
        }

        const selectedTrip = tripsData.tripGroups[navigation.selectedTripIndex];
        if (!selectedTrip || !selectedTrip.travelRequestId) {
            overrideStateHook.setOverrideState({
                isMilesOverWritten: false,
                overrideMiles: 0,
                isMealsOverWritten: false,
                overrideMeals: 0,
                isCarServiceExpenseOverridden: false,
                overrideCarServiceExpense: 0,
                isDependentCareExpenseOverridden: false,
                overrideDependentCareExpense: 0,
                isHotelLodgingExpenseOverridden: false,
                overrideHotelLodgingExpense: 0
            });
            return;
        }

        const travelRequest = selectedTrip.travelRequestId === pendingTravelRequest?.id 
            ? pendingTravelRequest 
            : props.data.requestTravels.find(tr => tr.id === selectedTrip.travelRequestId);
        overrideStateHook.syncWithTravelRequest(travelRequest);
    }, [navigation.viewType, navigation.selectedTripIndex, tripsData.tripGroups, pendingTravelRequest, props.data.requestTravels]);

    const showDeclineModal = useCallback((id: number, tripId: number, hasLineItemsSelected: boolean) => {
        context.onShowDeclineModal(id, true, tripId, hasLineItemsSelected);
    }, [context]);

    const selectedTrip = tripsData.tripGroups[navigation.selectedTripIndex];
    const allTripsApproved = useMemo(() => 
        tripsData.tripGroups.length > 0 && areAllTripsResolved(tripsData.tripGroups),
        [tripsData.tripGroups]
    );
    const approvedCount = tripsData.tripGroups.filter(trip => trip.status === 'approved').length;
    const declinedCount = tripsData.tripGroups.filter(trip => trip.status === 'declined').length;
    const resolvedCount = approvedCount + declinedCount;

    const trip = selectedTrip?.trip;
    const lineItems = navigation.viewType === 'overall'
        ? tripsData.tripGroups.flatMap(g => g.lineItems).filter(li => li.amount > 0)
        : (selectedTrip?.lineItems || []).filter(li => li.amount > 0);
    
    const allLineItemsForDependentCare = navigation.viewType === 'overall'
        ? tripsData.tripGroups.flatMap(g => g.lineItems).filter(li => li.expenseType === ExpenseType.DEPENDENT_CARE)
        : (selectedTrip?.lineItems || []).filter(li => li.expenseType === ExpenseType.DEPENDENT_CARE);
    const priorClosedClaims = useMemo(
        () => getPriorClosedTravelClaims(props.data?.requestTravels),
        [props.data?.requestTravels]
    );
    const disableClaimHistory = !hasPriorClosedTravelClaims(props.data?.requestTravels);

    const handleViewFile = useCallback(async (docType: number, tripId: number) => {
        let targetGroup: ITripGroup | null = null;
        
        if (navigation.viewType === 'overall' && areAllTripsResolved(tripsData.tripGroups)) {
            const allLineItems = tripsData.tripGroups.flatMap(g => g.lineItems);
            const lineItemsWithFiles = allLineItems.filter(li => li.receiptFileId);
            
            if (lineItemsWithFiles.length === 0) {
                return;
            }
            
            targetGroup = {
                id: 0,
                name: 'All Trips',
                type: 'overall',
                startDate: '',
                endDate: '',
                status: 'approved',
                trip: trip || tripsData.tripGroups[0]?.trip || null,
                lineItems: lineItemsWithFiles,
                travelRequestId: pendingTravelRequest?.id || 0
            };
        } else {
            targetGroup = tripsData.tripGroups[navigation.selectedTripIndex];
        }
        
        if (!targetGroup || !targetGroup.lineItems) {
            return;
        }

        if (!tripHasReviewableContent(targetGroup.lineItems)) {
            return;
        }

        setIsManualSelection(true);
        sidebarState.setSelectedButton(docType);

        try {
            await sidebarController.handleManualOpen(targetGroup, true);
        } catch (error) {
        }
        
        setTimeout(() => {
            setIsManualSelection(false);
        }, 600);
    }, [tripsData.tripGroups, navigation.selectedTripIndex, navigation.viewType, sidebarController, sidebarState, lastOpenedTripId, trip, pendingTravelRequest]);

    const handleViewClaimHistory = useCallback(() => {
        if (disableClaimHistory) {
            return;
        }
        setShowClaimHistoryModal(true);
    }, [disableClaimHistory]);

    const effectiveDerReimbursementLimit = useMemo(() => resolveDerProgramCap(props.data), [props.data]);
    const expenseTotals = useMemo(() => calculateExpenseTotals(lineItems || []), [lineItems]);
    const totalRequested = useMemo(() => (lineItems || []).reduce((sum, item) => sum + item.amount, 0), [lineItems]);
    const priorTotals = useMemo(() => getPriorTotals(props.data), [props.data]);
    const rawRequestedAmounts = useMemo(() => {
        const items = lineItems || [];
        return {
            lodging: items.filter(li => li.expenseType === ExpenseType.LODGING).reduce((sum, li) => sum + li.amount, 0),
            flight: items.filter(li => li.expenseType === ExpenseType.FLIGHT).reduce((sum, li) => sum + li.amount, 0),
            transportation: items.filter(li => li.expenseType === ExpenseType.TRANSPORTATION || li.expenseType === ExpenseType.CAR_SERVICE).reduce((sum, li) => sum + li.amount, 0),
            meals: items.filter(li => li.expenseType === ExpenseType.MEALS).reduce((sum, li) => sum + li.amount, 0),
            dependentCare: items.filter(li => li.expenseType === ExpenseType.DEPENDENT_CARE).reduce((sum, li) => sum + li.amount, 0)
        };
    }, [lineItems]);

    const mileageLineItemsForTotals = useMemo(() => {
        if (!selectedTrip) {
            return [];
        }

        return navigation.viewType === 'overall'
            ? tripsData.tripGroups.flatMap(g => g.lineItems).filter(li => li.expenseType === ExpenseType.MILEAGE)
            : (lineItems || []).filter(li => li.expenseType === ExpenseType.MILEAGE);
    }, [navigation.viewType, tripsData.tripGroups, lineItems, selectedTrip]);
    const totalOneWayMiles = useMemo(() => sumMileageRoundTripMiles(mileageLineItemsForTotals), [mileageLineItemsForTotals]);
    const totalMileageReimbursement = useMemo(() => sumMileageLineItemAmount(mileageLineItemsForTotals), [mileageLineItemsForTotals]);
    const mileageRate = useMemo(() => {
        const storedRate = deriveMileageRateFromLineItems(mileageLineItemsForTotals);

        if (storedRate > 0) {
            return storedRate;
        }

        return resolveMileageRate(trip?.reimbursementRate, trip?.tripDate as any);
    }, [mileageLineItemsForTotals, trip?.reimbursementRate, trip?.tripDate]);

    const approvedGrandTotal = useMemo(() => {
        const reimbursementRate = mileageRate;
        const useOverrides = navigation.viewType === 'overall';
        
        const mileageApproved = Math.min(
            useOverrides && overrideStateHook.overrideState.isMilesOverWritten && overrideStateHook.overrideState.overrideMiles > 0 
                ? overrideStateHook.overrideState.overrideMiles * reimbursementRate
                : totalMileageReimbursement,
            props.data.limit.mileage * reimbursementRate
        );
        const lodgingApproved = expenseTotals.lodging;
        const flightApproved = expenseTotals.flight;
        const transportationApproved = Math.min(
            useOverrides && overrideStateHook.overrideState.isCarServiceExpenseOverridden && overrideStateHook.overrideState.overrideCarServiceExpense > 0
                ? overrideStateHook.overrideState.overrideCarServiceExpense
                : expenseTotals.transportation,
            props.data.limit.transportationExpenses
        );
        const mealsApproved = Math.min(
            useOverrides && overrideStateHook.overrideState.isMealsOverWritten && overrideStateHook.overrideState.overrideMeals > 0
                ? overrideStateHook.overrideState.overrideMeals
                : expenseTotals.meals,
            props.data.limit.meals
        );
        const dependentCareApproved = Math.min(
            useOverrides && overrideStateHook.overrideState.isDependentCareExpenseOverridden && overrideStateHook.overrideState.overrideDependentCareExpense > 0
                ? overrideStateHook.overrideState.overrideDependentCareExpense
                : expenseTotals.dependentCare,
            props.data.limit.dependentCare
        );
        
        return mileageApproved + lodgingApproved + flightApproved + transportationApproved + mealsApproved + dependentCareApproved;
    }, [
        navigation.viewType,
        overrideStateHook.overrideState.isMilesOverWritten,
        overrideStateHook.overrideState.overrideMiles,
        overrideStateHook.overrideState.isCarServiceExpenseOverridden,
        overrideStateHook.overrideState.overrideCarServiceExpense,
        overrideStateHook.overrideState.isMealsOverWritten,
        overrideStateHook.overrideState.overrideMeals,
        overrideStateHook.overrideState.isDependentCareExpenseOverridden,
        overrideStateHook.overrideState.overrideDependentCareExpense,
        trip?.reimbursementRate,
        mileageRate,
        totalMileageReimbursement,
        expenseTotals.lodging,
        expenseTotals.flight,
        expenseTotals.transportation,
        expenseTotals.meals,
        expenseTotals.dependentCare,
        props.data.limit.mileage,
        props.data.limit.transportationExpenses,
        props.data.limit.meals,
        props.data.limit.dependentCare
    ]);

    const tripStats = useMemo(() => {
        const stats = calculateTripStats(selectedTrip, props.data);
        const remainingDer = Math.max(0, stats.derLimit);

        if (navigation.viewType !== 'overall') {
            return stats;
        }

        const stored = recalculateGrandTotal(tripsData.tripGroups);
        const pending = stored.pendingApprovedTotal + stored.caregiverPendingApprovedTotal;
        const approved = stored.currentlyApprovedTotal + stored.caregiverCurrentlyApprovedTotal;

        return {
            ...stats,
            currentPendingReimbursementAmount: Math.min(pending, remainingDer),
            currentlyApproved: approved
        };
    }, [selectedTrip, props.data, navigation.viewType, tripsData.tripGroups]);

    const handleTripSelect = useCallback(async (index: number, tripGroup: ITripGroup) => {
        if (tripsData.isLoading) {
            return;
        }
        
        setIsManualSelection(true);
        
        if (sidebarController.isSidebarOpen) {
            sidebarController.handleManualClose();
            await new Promise(resolve => setTimeout(resolve, 150));
        }
        
        navigation.setSelectedTripIndex(index);
        navigation.setViewType('trip');
        sidebarState.setActiveExpenseType(null);

        if (tripHasReviewableContent(tripGroup.lineItems)) {
            try {
                await sidebarController.handleManualOpen(tripGroup, true);
            } catch (error) {
            }
        } else {
            if (context?.onHideDataSideBar) {
                context.onHideDataSideBar();
            }

            setLastOpenedTripId(tripGroup.id);
        }
        
        setIsManualSelection(false);
    }, [navigation, sidebarState, sidebarController, tripsData.isLoading, context]);

    const handleApproveClaim = useCallback(async () => {
        if (!pendingTravelRequest || claimVerification.isApprovingClaim) {
            return;
        }

        const allLineItems = tripsData.tripGroups.flatMap(g => g.lineItems);

        try {
            if (context?.showLoading) {
                await context.showLoading('Approving claim…');
            }

            const result = await claimVerification.approveClaim(
                pendingTravelRequest.id,
                tripsData.tripGroups.map(g => g.trip),
                allLineItems,
                overrideStateHook.overrideState,
                props.data.limit.hotelNights,
                props.data.limit,
                mileageRate
            );

            if (result?.success) {
                toast.success('Claim approved and saved successfully');
                sidebarState.closeSidebar();
                sidebarController.handleManualClose();

                if (context?.onHideDataSideBar) {
                    context.onHideDataSideBar();
                }

                if (context?.onCloseRequestView) {
                    context.onCloseRequestView();
                }

                if (context?.onRefreshList) {
                    await context.onRefreshList();
                } else if (props.onRequestRefresh) {
                    await props.onRequestRefresh();
                }
            } else if (result?.error) {
                toast.error(result.error);
            }
        } finally {
            if (context?.hideLoading) {
                context.hideLoading();
            }
        }
    }, [
        pendingTravelRequest,
        props.data.limit.hotelNights,
        tripsData,
        claimVerification,
        overrideStateHook.overrideState,
        sidebarState,
        sidebarController,
        props.onRequestRefresh,
        mileageRate,
        context
    ]);

    if (!pendingTravelRequest) {
        return (
            <VerifyWrapper>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>No pending travel requests to verify.</p>
                </div>
            </VerifyWrapper>
        );
    }

    if (tripsData.isLoading || tripsData.tripGroups.length === 0) {
        return (
            <VerifyWrapper>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>{tripsData.isLoading ? 'Loading trips...' : 'No trips found.'}</p>
                </div>
            </VerifyWrapper>
        );
    }

    if (!selectedTrip) {
        return (
            <VerifyWrapper>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>No trips available.</p>
                </div>
            </VerifyWrapper>
        );
    }

    const travelRequest = selectedTrip.travelRequestId === pendingTravelRequest.id 
        ? pendingTravelRequest 
        : props.data.requestTravels.find(tr => tr.id === selectedTrip.travelRequestId) || pendingTravelRequest;
    const mockTravelRequest: IWatrRequestTravels & { isHotelLodgingExpenseOverridden?: boolean; overrideHotelLodgingExpense?: number } = travelRequest ? {
        ...travelRequest,
        roundTripMiles: totalOneWayMiles,
        hotelLodgingExpense: expenseTotals.lodging,
        flightAndTrainExpense: expenseTotals.flight,
        carServiceExpense: expenseTotals.transportation,
        dependentCareExpense: expenseTotals.dependentCare,
        mealExpense: expenseTotals.meals,
        isCarServiceExpenseOverridden: overrideStateHook.overrideState.isCarServiceExpenseOverridden || travelRequest.isCarServiceExpenseOverridden || false,
        overrideCarServiceExpense: overrideStateHook.overrideState.isCarServiceExpenseOverridden 
            ? (overrideStateHook.overrideState.overrideCarServiceExpense !== undefined && overrideStateHook.overrideState.overrideCarServiceExpense !== null && overrideStateHook.overrideState.overrideCarServiceExpense > 0
                ? overrideStateHook.overrideState.overrideCarServiceExpense 
                : (travelRequest.overrideCarServiceExpense > 0 ? travelRequest.overrideCarServiceExpense : (rawRequestedAmounts.transportation > 0 ? rawRequestedAmounts.transportation : 0)))
            : (travelRequest.overrideCarServiceExpense || 0),
        isDependentCareExpenseOverridden: overrideStateHook.overrideState.isDependentCareExpenseOverridden || travelRequest.isDependentCareExpenseOverridden || false,
        overrideDependentCareExpense: overrideStateHook.overrideState.isDependentCareExpenseOverridden 
            ? (overrideStateHook.overrideState.overrideDependentCareExpense !== undefined && overrideStateHook.overrideState.overrideDependentCareExpense !== null && overrideStateHook.overrideState.overrideDependentCareExpense > 0
                ? overrideStateHook.overrideState.overrideDependentCareExpense 
                : (travelRequest.overrideDependentCareExpense > 0 ? travelRequest.overrideDependentCareExpense : (rawRequestedAmounts.dependentCare > 0 ? rawRequestedAmounts.dependentCare : 0)))
            : (travelRequest.overrideDependentCareExpense || 0),
        isMealsOverWritten: overrideStateHook.overrideState.isMealsOverWritten || travelRequest.isMealsOverWritten || false,
        overrideMeals: overrideStateHook.overrideState.isMealsOverWritten 
            ? (overrideStateHook.overrideState.overrideMeals !== undefined && overrideStateHook.overrideState.overrideMeals !== null && overrideStateHook.overrideState.overrideMeals > 0
                ? overrideStateHook.overrideState.overrideMeals 
                : (travelRequest.overrideMeals > 0 ? travelRequest.overrideMeals : (rawRequestedAmounts.meals > 0 ? rawRequestedAmounts.meals : 0)))
            : (travelRequest.overrideMeals || 0),
        isMilesOverWritten: overrideStateHook.overrideState.isMilesOverWritten || travelRequest.isMilesOverWritten || false,
        overrideMiles: overrideStateHook.overrideState.isMilesOverWritten 
            ? (overrideStateHook.overrideState.overrideMiles !== undefined && overrideStateHook.overrideState.overrideMiles !== null 
                ? overrideStateHook.overrideState.overrideMiles 
                : (travelRequest.overrideMiles || 0))
            : (travelRequest.overrideMiles || 0),
        isHotelLodgingExpenseOverridden: overrideStateHook.overrideState.isHotelLodgingExpenseOverridden || (travelRequest as any).isHotelLodgingExpenseOverridden || false,
        overrideHotelLodgingExpense: overrideStateHook.overrideState.isHotelLodgingExpenseOverridden 
            ? (overrideStateHook.overrideState.overrideHotelLodgingExpense !== undefined && overrideStateHook.overrideState.overrideHotelLodgingExpense !== null && overrideStateHook.overrideState.overrideHotelLodgingExpense > 0
                ? overrideStateHook.overrideState.overrideHotelLodgingExpense 
                : ((travelRequest as any).overrideHotelLodgingExpense > 0 ? (travelRequest as any).overrideHotelLodgingExpense : (rawRequestedAmounts.lodging > 0 ? rawRequestedAmounts.lodging : 0)))
            : ((travelRequest as any).overrideHotelLodgingExpense || 0),
    } : {
        id: pendingTravelRequest.id,
        requestId: props.data.id,
        reimbursementId: pendingTravelRequest.reimbursementId || 0,
        status: pendingTravelRequest.status,
        createdDateTime: trip.createdDateTime ? moment(trip.createdDateTime).toDate() : new Date(),
        lastUpdated: trip.lastUpdated ? moment(trip.lastUpdated).toDate() : new Date(),
        addressLine1: props.data.addressLine1 || '',
        city: props.data.city || '',
        state: props.data.state as any,
        postalCode: props.data.postalCode || '',
        roundTripMiles: totalOneWayMiles,
        roundTripInstances: [],
        roundTripInstancesJson: '',
        declines: [],
        hotelLodgingExpense: expenseTotals.lodging,
        flightAndTrainExpense: expenseTotals.flight,
        carServiceExpense: expenseTotals.transportation,
        isCarServiceExpenseOverridden: overrideStateHook.overrideState.isCarServiceExpenseOverridden,
        overrideCarServiceExpense: overrideStateHook.overrideState.overrideCarServiceExpense,
        dependentCareExpense: expenseTotals.dependentCare,
        isDependentCareExpenseOverridden: overrideStateHook.overrideState.isDependentCareExpenseOverridden,
        overrideDependentCareExpense: overrideStateHook.overrideState.overrideDependentCareExpense,
        mealExpense: expenseTotals.meals,
        isMealsOverWritten: overrideStateHook.overrideState.isMealsOverWritten,
        overrideMeals: overrideStateHook.overrideState.overrideMeals,
        isMilesOverWritten: overrideStateHook.overrideState.isMilesOverWritten,
        overrideMiles: overrideStateHook.overrideState.overrideMiles,
        isHotelLodgingExpenseOverridden: overrideStateHook.overrideState.isHotelLodgingExpenseOverridden,
        overrideHotelLodgingExpense: overrideStateHook.overrideState.overrideHotelLodgingExpense,
        reimbursableMiles: totalOneWayMiles,
        reimbursableMeals: expenseTotals.meals,
        totalMileageRequested: totalOneWayMiles,
        achRoutingNumber: props.data.achRoutingNumber || '',
        achAccountNumber: props.data.achAccountNumber || '',
        requireDependentCare: false,
        requireDependentCompanion: false,
        requirePaymentAssistance: false,
        dependentCareFile: '',
        dependentCareFileUploadDate: null,
        dependentCompanionFile: '',
        dependentCompanionFileUploadDate: null,
        paymentAssistanceFile: '',
        paymentAssistanceFileUploadDate: null,
        travelReceiptsFile: '',
        travelReceiptsFileUploadDate: null,
        travelReceiptsTotal: totalRequested,
        travelReimbursementTotal: totalRequested,
        tlr: totalRequested,
        reimbursementAmount: 0,
        previousReimbursementAmount: 0,
        remainingReimbursementAmount: 0,
        allowedReimbursementAmount: 0,
        lineItems: [],
        trips: []
    };

    const loadingOverlay = isSwitchingTrips ? (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            flexDirection: 'column',
            gap: '16px'
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: '24px 32px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                minWidth: '300px'
            }}>
                <Glyphicon glyph="refresh" className="spinning" style={{ fontSize: '32px', color: '#337ab7' }} />
                <div style={{ fontSize: '16px', fontWeight: 500, color: '#333', textAlign: 'center' }}>
                    Processing trip information...
                </div>
            </div>
        </div>
    ) : null;

    return (
        <>
            {typeof document !== 'undefined' && createPortal(loadingOverlay, document.body)}
            <ConnectedDevMockDocsOffer
                data={props.data}
                downloadFile={(docType, lookupId) => context ? context.downloadFile(docType, lookupId as number) : Promise.resolve()}
                receiptFileIds={receiptFileIdsForMocks}
                receiptInputs={receiptInputsForMocks}
                includeWages={false}
                includeReceipts={true}
                onComplete={() => {
                    refreshLineItems();
                }}
            />
            {claimVerification.claimApproved && (
                <Alert bsStyle="success" style={{ marginBottom: '16px' }}>
                    <strong>Claim Approved</strong> - The travel reimbursement claim has been successfully approved.
                </Alert>
            )}
            <Header data={props.data} travel={mockTravelRequest} showStatus={false} showEmploymentType={false} />

            <TripPhaseTabs
                tripGroups={tripsData.tripGroups}
                selectedTripIndex={navigation.selectedTripIndex}
                viewType={navigation.viewType}
                allTripsApproved={allTripsApproved}
                resolvedCount={resolvedCount}
                onTripSelect={handleTripSelect}
                onFinalizeSelect={() => {
                    if (!allTripsApproved) {
                        return;
                    }

                    setIsManualSelection(true);
                    navigation.setViewType('overall');
                    setTimeout(() => {
                        setIsManualSelection(false);
                    }, 600);
                }}
            />

            <VerifyWrapper>
                <div>
                    <DonorExpenseRequestSummary
                        data={mockTravelRequest}
                        limit={props.data.limit}
                        effectiveDerReimbursementLimit={effectiveDerReimbursementLimit}
                        lineItems={lineItems}
                        activeExpenseType={sidebarState.activeExpenseType}
                        approvedExpenseTypes={sidebarState.approvedExpenseTypes}
                        approvedLineItemIds={sidebarState.approvedLineItemIds}
                        activeExpenseTypeFileIndex={sidebarState.activeExpenseTypeFileIndex}
                        onOpenMileageReview={async () => {
                            const tripGroup = tripsData.tripGroups[navigation.selectedTripIndex];

                            if (!tripGroup) {
                                return;
                            }

                            setIsManualSelection(true);
                            setLastOpenedTripId(tripGroup.id);
                            sidebarController.markSidebarOpenForTrip(tripGroup.id);

                            try {
                                await sidebarState.openMileageReview(tripGroup);
                            } finally {
                                setTimeout(() => {
                                    setIsManualSelection(false);
                                }, 600);
                            }
                        }}
                        isReadOnly={navigation.viewType === 'overall'}
                        viewType={navigation.viewType}
                        tripId={navigation.viewType === 'trip' ? trip?.id : null}
                        allTripIds={navigation.viewType === 'overall' ? tripsData.tripGroups.map(g => g.trip.id) : undefined}
                        totalReimbursementAmount={totalMileageReimbursement}
                        totalOneWayMiles={totalOneWayMiles}
                        mileageRate={mileageRate}
                        totalMilesReimbursementAmountWithOverride={navigation.viewType === 'overall' 
                            ? calculateTotalMilesReimbursementAmountWithOverride(overrideStateHook.overrideState, totalMileageReimbursement, mileageRate)
                            : totalMileageReimbursement}
                        totalMealsReimbursementAmountWithOverride={navigation.viewType === 'overall'
                            ? calculateTotalMealsReimbursementAmountWithOverride(overrideStateHook.overrideState, rawRequestedAmounts, expenseTotals)
                            : expenseTotals.meals}
                        totalCarServiceExpenseReimbursementAmountWithOverride={navigation.viewType === 'overall'
                            ? calculateTotalCarServiceExpenseReimbursementAmountWithOverride(overrideStateHook.overrideState, rawRequestedAmounts, expenseTotals)
                            : expenseTotals.transportation}
                        totalDependentCarExpenseReimbursementAmountWithOverride={navigation.viewType === 'overall'
                            ? calculateTotalDependentCarExpenseReimbursementAmountWithOverride(overrideStateHook.overrideState, rawRequestedAmounts, expenseTotals)
                            : expenseTotals.dependentCare}
                        hasAdvancedReimbursement={hasDonorAdvancedReimbursement(props.data)}
                        requestedGrandTotal={totalRequested}
                        approvedGrandTotal={approvedGrandTotal}
                        priorTotals={priorTotals}
                        onChange={(e, id) => {
                        }}
                        onBlur={(e, id) => {
                            if (navigation.viewType !== 'overall') {
                                return;
                            }
                            const target = e.currentTarget;
                            const value = parseFloat(target.value) || 0;
                            const name = target.name;
                            
                            if (name === 'overrideMiles') {
                                overrideStateHook.updateOverride({ overrideMiles: value });
                            } else if (name === 'overrideMeals') {
                                overrideStateHook.updateOverride({ overrideMeals: value });
                            } else if (name === 'overrideCarServiceExpense') {
                                overrideStateHook.updateOverride({ overrideCarServiceExpense: value });
                            } else if (name === 'overrideDependentCareExpense') {
                                overrideStateHook.updateOverride({ overrideDependentCareExpense: value });
                            } else if (name === 'overrideHotelLodgingExpense') {
                                overrideStateHook.updateOverride({ overrideHotelLodgingExpense: value });
                            }
                        }}
                        onOverride={navigation.viewType === 'overall' ? (id, overrideType) => {
                            switch (overrideType) {
                                case Override.Miles:
                                    overrideStateHook.toggleOverride('miles', totalOneWayMiles || 0);
                                    break;
                                case Override.Meals:
                                    overrideStateHook.toggleOverride('meals', rawRequestedAmounts.meals || 0);
                                    break;
                                case Override.carService:
                                    overrideStateHook.toggleOverride('carService', rawRequestedAmounts.transportation || 0);
                                    break;
                                case Override.DependentCare:
                                    overrideStateHook.toggleOverride('dependentCare', rawRequestedAmounts.dependentCare || 0);
                                    break;
                                case Override.Lodging:
                                    overrideStateHook.toggleOverride('lodging', rawRequestedAmounts.lodging || 0);
                                    break;
                            }
                        } : undefined}
                        onSetReviewRequired={context.onSetReviewRequiredBulk ? async (lineItemIds: number[], reviewRequired: boolean) => {
                            await context.onSetReviewRequiredBulk!(lineItemIds, reviewRequired);
                        } : undefined}
                        onReviewRequiredStateChange={(lineItemIds: number[], reviewRequired: boolean) => {
                            reviewState.updateReviewRequired(lineItemIds, reviewRequired);
                        }}
                        reviewRequiredState={reviewState.reviewRequiredState}
                        onCaregiverToggle={(lineItemId: number, isCaregiver: boolean) => {
                            reviewState.updateLineItem(lineItemId, { isCaregiverExpense: isCaregiver });
                        }}
                        lineItemState={reviewState.lineItemState}
                        onUpdateLineItem={(lineItemId: number, updates: Partial<ILineItem>) => {
                            reviewState.updateLineItem(lineItemId, updates);
                        }}
                    />

                    <TripStatsCards
                        tripStats={tripStats}
                        viewType={navigation.viewType}
                        allTripsApproved={allTripsApproved}
                        onViewTypeChange={navigation.setViewType}
                    />

                    {(navigation.viewType === 'trip' || navigation.viewType === 'overall') && (
                        <Row style={{ marginBottom: '16px' }}>
                            <Col md={6} xs={12} style={{ marginBottom: '12px' }}>
                                <CaregiverInfoCard 
                                    trip={trip} 
                                    lineItems={navigation.viewType === 'overall' 
                                        ? tripsData.tripGroups.flatMap(g => g.lineItems)
                                        : (selectedTrip?.lineItems || [])} 
                                />
                            </Col>
                            <Col md={6} xs={12} style={{ marginBottom: '12px' }}>
                                <DependentCareInfoCard 
                                    lineItems={navigation.viewType === 'overall' 
                                        ? tripsData.tripGroups.flatMap(g => g.lineItems)
                                        : (selectedTrip?.lineItems || [])} 
                                />
                            </Col>
                        </Row>
                    )}

                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            gap: '12px',
                            padding: '18px 20px',
                            marginTop: '16px',
                            background: '#fafbfc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', minWidth: '110px' }}>
                            {navigation.viewType === 'overall' && allTripsApproved ? (() => {
                                const allLineItems = tripsData.tripGroups.flatMap(g => g.lineItems);
                                const disableState = checkClaimApprovalDisabled(
                                    tripsData.tripGroups,
                                    allLineItems,
                                    reviewState.reviewRequiredState
                                );
                                disableState.isProcessing = claimVerification.isApprovingClaim || claimVerification.isDecliningClaim;
                                const isDeclineClaimDisabled = isDeclineDisabled(disableState);

                                return (
                                    <Button
                                        bsStyle="danger"
                                        disabled={isDeclineClaimDisabled}
                                        style={{ width: 110 }}
                                        onClick={() => {
                                            if (!pendingTravelRequest) {
                                                return;
                                            }
                                            const hasLineItemsSelected = allLineItems.some(li => {
                                                const stateValue = reviewState.reviewRequiredState.get(li.id);
                                                return stateValue !== undefined ? stateValue === true : li.reviewRequired === true;
                                            });
                                            if (!hasLineItemsSelected) {
                                                setUnpayableClaimConfirm({
                                                    show: true,
                                                    requestId: props.data.id,
                                                    travelId: pendingTravelRequest.id
                                                });
                                                return;
                                            }
                                            showDeclineModal(props.data.id, pendingTravelRequest.id, true);
                                        }}
                                    >
                                        {claimVerification.isDecliningClaim ? 'Declining...' : 'Decline'}
                                    </Button>
                                );
                            })() : (() => {
                                const disableState = checkTripApprovalDisabled(
                                    selectedTrip,
                                    reviewState.reviewRequiredState
                                );
                                disableState.isProcessing = tripApproval.isApprovingTrip || tripApproval.isDecliningTrip;
                                const tripDeclineDisabled = isDeclineDisabled(disableState);

                                return (
                                    <Button
                                        bsStyle="danger"
                                        disabled={tripDeclineDisabled}
                                        style={{ width: 110 }}
                                        onClick={() => {
                                            if (!trip || !selectedTrip) {
                                                return;
                                            }
                                            const hasLineItemsSelected = selectedTrip.lineItems.some(li => {
                                                const stateValue = reviewState.reviewRequiredState.get(li.id);
                                                return stateValue !== undefined ? stateValue === true : li.reviewRequired === true;
                                            });
                                            if (!hasLineItemsSelected) {
                                                setUnpayableTripConfirm({
                                                    show: true,
                                                    tripId: trip.id,
                                                    requestId: props.data.id,
                                                    tripIndex: navigation.selectedTripIndex
                                                });
                                                return;
                                            }
                                            tripApproval.declineTrip(trip.id, props.data.id, selectedTrip, navigation.selectedTripIndex);
                                        }}
                                    >
                                        {tripApproval.isDecliningTrip ? 'Declining...' : 'Decline'}
                                    </Button>
                                );
                            })()}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                <Button
                                    className={sidebarState.selectedButton === FileDocumentType.Receipts ? 'selected-btn' : ''}
                                    onClick={() => {
                                        handleViewFile(FileDocumentType.Receipts, trip.id);
                                    }}
                                >
                                    View Receipts
                                </Button>
                                <Button
                                    disabled={disableClaimHistory}
                                    title={disableClaimHistory ? 'There are no previous claims' : 'Click here to see previous claims'}
                                    onClick={handleViewClaimHistory}
                                >
                                    {priorClosedClaims.length > 0
                                        ? `View Claim History (${priorClosedClaims.length})`
                                        : 'View Claim History'}
                                </Button>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {navigation.viewType === 'overall' && allTripsApproved ? (() => {
                                    const allLineItems = tripsData.tripGroups.flatMap(g => g.lineItems);
                                    const disableState = checkClaimApprovalDisabled(
                                        tripsData.tripGroups,
                                        allLineItems,
                                        reviewState.reviewRequiredState
                                    );
                                    disableState.isProcessing = claimVerification.isApprovingClaim || claimVerification.isDecliningClaim;
                                    const isApproveClaimDisabled = isApproveDisabled(disableState);

                                    return (
                                        <>
                                            {disableState.hasPendingLineItemChanges && (
                                                <Alert bsStyle="warning" style={{ margin: 0, padding: '8px 12px', fontSize: '12px' }}>
                                                    <strong>Pending Unsaved Changes</strong>
                                                </Alert>
                                            )}
                                            <Button
                                                bsStyle="success"
                                                disabled={isApproveClaimDisabled}
                                                style={{ minWidth: 160 }}
                                                onClick={handleApproveClaim}
                                            >
                                                {claimVerification.isApprovingClaim ? 'Approving claim…' : 'Approve Claim'}
                                            </Button>
                                        </>
                                    );
                                })() : (() => {
                                    const disableState = checkTripApprovalDisabled(
                                        selectedTrip,
                                        reviewState.reviewRequiredState
                                    );
                                    disableState.isProcessing = tripApproval.isApprovingTrip || tripApproval.isDecliningTrip;
                                    const tripApproveDisabled = isApproveDisabled(disableState);

                                    return (
                                        <>
                                            {disableState.hasPendingLineItemChanges && (
                                                <Alert bsStyle="warning" style={{ margin: 0, padding: '8px 12px', fontSize: '12px' }}>
                                                    <strong>Pending Unsaved Changes</strong>
                                                </Alert>
                                            )}
                                            <Button
                                                bsStyle="success"
                                                disabled={tripApproveDisabled}
                                                style={{ width: 120 }}
                                                onClick={() => tripApproval.approveTrip(trip.id, props.data.id, selectedTrip, navigation.selectedTripIndex)}
                                            >
                                                {tripApproval.isApprovingTrip ? 'Approving...' : 'Approve Trip'}
                                            </Button>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            </VerifyWrapper>
            <ConfirmUnpayableDecline
                show={unpayableClaimConfirm.show}
                title="Confirm Unpayable Decline"
                messages={[
                    'No expenses have been marked for review.',
                    'The request will be updated as Unpayable and the donor will not be able to resubmit.',
                    'Please confirm decline.'
                ]}
                confirmLabel="Confirm Decline"
                onCancel={() => setUnpayableClaimConfirm({ show: false, requestId: 0, travelId: 0 })}
                onConfirm={() => {
                    const { requestId, travelId } = unpayableClaimConfirm;
                    setUnpayableClaimConfirm({ show: false, requestId: 0, travelId: 0 });
                    showDeclineModal(requestId, travelId, false);
                }}
            />
            <ConfirmUnpayableDecline
                show={unpayableTripConfirm.show}
                title="Confirm Decline Without Review Items"
                messages={[
                    'No expenses have been marked for review.',
                    'Please confirm decline.'
                ]}
                confirmLabel="Confirm Decline"
                onCancel={() => setUnpayableTripConfirm({ show: false, tripId: 0, requestId: 0, tripIndex: 0 })}
                onConfirm={() => {
                    const { tripId, requestId, tripIndex } = unpayableTripConfirm;
                    const tripGroup = tripsData.tripGroups[tripIndex] || selectedTrip;
                    setUnpayableTripConfirm({ show: false, tripId: 0, requestId: 0, tripIndex: 0 });
                    if (tripGroup) {
                        tripApproval.declineTrip(tripId, requestId, tripGroup, tripIndex);
                    }
                }}
            />
            <ClaimHistoryModal
                show={showClaimHistoryModal}
                data={props.data}
                onClose={() => setShowClaimHistoryModal(false)}
            />
        </>
    );
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        getTripsByTravelRequestId: (travelRequestId) => {
            return dispatch(WatrActionCreators.getTripsByTravelRequestId(travelRequestId));
        },
        getLineItemsByTripId: (tripId) => {
            return dispatch(WatrActionCreators.getLineItemsByTripId(tripId));
        },
        approveTrip: (tripId, requestId, requiresEmail, lineItemReviewRequiredUpdates) => {
            return dispatch(WatrActionCreators.approveTrip(tripId, requestId, requiresEmail, lineItemReviewRequiredUpdates));
        },
        declineTrip: (tripId, requestId, requiresEmail, lineItemUpdates) => {
            return dispatch(WatrActionCreators.declineTrip(tripId, requestId, requiresEmail, lineItemUpdates));
        },
        verifyTravel: (travelRequestId, roundTripMiles, hotelLodgingExpense, flightAndTrainExpense, carServiceExpense, dependentCareExpense,
            mealExpense, isMilesOverWritten, overrideMiles, isMealsOverWritten, overrideMeals,
            isCarServiceExpenseOverridden, overrideCarServiceExpense,
            isDependentCareExpenseOverridden, overrideDependentCareExpense,
            isHotelLodgingExpenseOverridden, overrideHotelLodgingExpense, approvalEmail) => {
            return dispatch(WatrActionCreators.verifyTravel(travelRequestId, roundTripMiles, hotelLodgingExpense, flightAndTrainExpense, carServiceExpense, dependentCareExpense,
                mealExpense, isMilesOverWritten, overrideMiles, isMealsOverWritten, overrideMeals,
                isCarServiceExpenseOverridden, overrideCarServiceExpense,
                isDependentCareExpenseOverridden, overrideDependentCareExpense,
                isHotelLodgingExpenseOverridden, overrideHotelLodgingExpense, approvalEmail));
        }
    };
};

export default connect<{}, IDispatchProps, IProps>(null, mapDispatchToProps)(VerifyTravel);

const spinningStyle = `
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
    .spinning {
        animation: spin 1s linear infinite;
    }
`;

if (typeof document !== 'undefined' && !document.getElementById('verify-travel-spinning-style')) {
    const style = document.createElement('style');
    style.id = 'verify-travel-spinning-style';
    style.textContent = spinningStyle;
    document.head.appendChild(style);
}